import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { LeagueSelect } from "@/components/LeagueSelect";
import { useAva } from "@/context/AvaContext";
import { Loader2, Sparkles, CalendarClock, ArrowRight, BookOpen } from "lucide-react";
import { toast } from "sonner";

const selectCls = "rounded-xl border border-white/10 bg-[#0a0a0a] px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50";

const fmtDate = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Fixtures() {
  const { speak } = useAva();
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [league, setLeague] = useState(39);
  const [fixtures, setFixtures] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preds, setPreds] = useState({});
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/leagues").then((r) => setLeagues(r.data)); }, []);
  useEffect(() => {
    setLoading(true); setPreds({}); setGroups([]); setFixtures([]);
    if (league === "all") {
      api.get(`/fixtures/all?n=6`).then((r) => setGroups(r.data.groups)).finally(() => setLoading(false));
    } else {
      api.get(`/fixtures?league=${league}&n=12`).then((r) => setFixtures(r.data)).finally(() => setLoading(false));
    }
  }, [league]);

  const predict = async (f) => {
    setBusy(f.fixture_id);
    try {
      const r = await api.post("/advisor/quick", { home_id: f.home.id, away_id: f.away.id });
      setPreds((p) => ({ ...p, [f.fixture_id]: r.data }));
      speak(`For ${f.home.name} versus ${f.away.name}, OCTON predicts ${f.home.name} in a ${r.data.home.formation} and ${f.away.name} in a ${r.data.away.formation}.`);
    } catch (e) {
      toast.error("Prediction failed");
    } finally { setBusy(null); }
  };

  const Side = ({ team, p, align }) => (
    <div className={`flex-1 ${align === "right" ? "text-right" : ""}`}>
      <div className={`flex items-center gap-2 ${align === "right" ? "justify-end" : ""}`}>
        <img src={team.logo} className="h-7 w-7 object-contain" alt="" />
        <span className="truncate text-sm text-white">{team.name}</span>
      </div>
      {p && (
        <div className="mt-2">
          <span className="font-head text-2xl font-black txt-cyan">{p.formation}</span>
          <span className="ml-2 font-mono text-xs" style={{ color: "#ccff00" }} title="Shape belief — not match win %">{p.win_prob}% <span className="text-white/35">shape</span></span>
          <p className="mt-1 text-[10px] text-white/40">{p.style}</p>
          <p className="mt-1 text-[10px] text-white/50">{p.key.slice(0, 3).join(" · ")}</p>
        </div>
      )}
    </div>
  );

  const renderFixture = (f, i) => {
    const p = preds[f.fixture_id];
    return (
      <motion.div key={f.fixture_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }}
        className="card-tac p-4" data-testid={`fixture-${f.fixture_id}`}>
        <div className="mb-3 flex items-center gap-2 text-[11px] text-white/40">
          <CalendarClock size={13} /> {fmtDate(f.date)} · {f.round}{f.venue ? <span className="text-white/40" data-testid="fixture-venue"> · {f.venue}{f.city ? `, ${f.city}` : ""}</span> : null}
        </div>
        <div className="flex items-center gap-3">
          <Side team={f.home} p={p?.home} align="left" />
          <span className="font-tac text-[10px] text-white/30">VS</span>
          <Side team={f.away} p={p?.away} align="right" />
        </div>
        {f.codex_angle && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-cyan-400/15 bg-cyan-400/[0.04] px-2.5 py-2" data-testid={`codex-angle-${f.fixture_id}`}>
            <BookOpen size={12} className="mt-0.5 shrink-0 txt-cyan" />
            <p className="text-[11px] leading-snug text-white/60"><span className="text-cyan-200">{f.codex_angle.title}:</span> {f.codex_angle.principle}</p>
          </div>
        )}
        <div className="mt-3 flex items-center justify-end gap-2">
          <button data-testid={`predict-${f.fixture_id}`} onClick={() => predict(f)} disabled={busy === f.fixture_id}
            className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-4 py-2 text-xs text-cyan-200 hover:bg-cyan-400/10 disabled:opacity-40">
            {busy === f.fixture_id ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {p ? "Re-predict" : "Predict formations"}
          </button>
          <button onClick={() => navigate("/advisor", { state: { league: f.league_id || league, team_id: f.home.id, opponent_id: f.away.id, autorun: true } })}
            className="inline-flex items-center gap-1.5 rounded-full bg-cyan-400 px-4 py-2 text-xs font-semibold text-black hover:shadow-[0_0_20px_rgba(0,229,255,0.5)]">
            Full brief <ArrowRight size={13} />
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="px-6 pb-24 pt-20 sm:px-10 lg:px-16">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-tac text-[11px] tracking-[0.35em] txt-cyan">MATCHWEEK · LIVE FIXTURES</p>
          <h1 className="font-head mt-2 text-5xl font-black uppercase tracking-tighter">Upcoming Fixtures</h1>
          <p className="mt-2 max-w-xl text-sm text-white/50">Real upcoming games across every league, each with OCTON's codex-grounded tactical angle. Tap a fixture for the full brief.</p>
        </div>
        <LeagueSelect testId="fixtures-league-select" value={league} onChange={(id) => setLeague(id)} allowAll />
      </div>

      {loading ? (
        <div className="mt-8 flex items-center gap-2 text-white/50"><Loader2 className="animate-spin" size={18} /> Loading real fixtures…</div>
      ) : league === "all" ? (
        groups.length === 0 ? (
          <p className="mt-8 text-white/50" data-testid="fixtures-empty" data-octon-section="fixtures empty none scheduled" data-octon-label="Fixtures">No upcoming fixtures available right now (the daily data quota may be spent — try again after it resets).</p>
        ) : (
          <div className="mt-6 space-y-8" data-testid="fixtures-all" data-octon-section="fixtures all leagues upcoming matches" data-octon-label="All-League Fixtures">
            {groups.map((g) => (
              <div key={g.league_id}>
                <div className="mb-3 flex items-center gap-2">
                  <span className="font-head text-xl font-black tracking-tight text-white">{g.league}</span>
                  <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-white/40">{g.fixtures.length} fixtures</span>
                </div>
                <div className="grid gap-3 lg:grid-cols-2">
                  {g.fixtures.map((f, i) => renderFixture({ ...f, league_id: g.league_id }, i))}
                </div>
              </div>
            ))}
          </div>
        )
      ) : fixtures.length === 0 ? (
        <p className="mt-8 text-white/50">No upcoming fixtures scheduled for this competition right now.</p>
      ) : (
        <div className="mt-6 space-y-3">
          {fixtures.map((f, i) => renderFixture(f, i))}
        </div>
      )}
    </div>
  );
}
